import React, { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import Pagination from '@mui/material/Pagination';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import { useDispatch, useSelector } from 'react-redux';
import { Icon } from '@iconify/react/dist/iconify.js';
import { clearTutorMatchingData, getStudentList, getTutorList } from '../../../../redux/features/CRDslice';
import AssignTutor from '../findMyTutor/AssignTutor';
import TutorDetails from '../findMyTutor/TutorDetails';
import TpDetails from '../findMyTutor/TpDetails';
import TutorCard from './TutorCard';
import TutorMatchingStudentDetails from './components/StudentDetails';
import GlobalSelectFieldWithoutFormikNew from '../../../../components/mui/GlobalSelectFieldWithoutFormikNew';
import GlobalDialog from '../../../../components/mui/Dialogue/GlobalDialog';
import UpdateSlots from './components/UpdateSlots';
import EditUpdateSlot from './components/EditUpdateSlot';

const RESPONSE_OPTIONS = [
  { id: -1, label: 'All' },
  { id: 0, label: 'No Response' },
  { id: 1, label: 'Responded' },
];
const ITEMS_PER_PAGE = 10;
const NO_IDS = [];

// Tutor search + cards for one student subject (the matching page's filters, list, profile / TP history / slots and
// Assign). Used by the CRD Tutor Match Making page and by the retention workspace (stage 4), which pre-selects the
// subject and drops earlier attempts' tutors via excludeTrainerIds. onAssigned(tutor) runs after a successful assign.
const TutorMatchPanel = ({ courseEnrollmentId, subjectEnrollmentId, excludeTrainerIds = NO_IDS, onAssigned }) => {
  const dispatch = useDispatch();
  const tutorMatch = useSelector((store) => store.crd.tutorMatch);
  const tutorMatchingLoading = useSelector((store) => store.crd.tutorMatch?.loading);

  const [responseFilter, setResponseFilter] = useState({ id: -1, value: '' });
  const [includeTimingPreference, setIncludeTimingPreference] = useState(false);
  const [rowDetails, setRowDetails] = useState(null);
  const [assignTutorOpen, setAssignTutorOpen] = useState(false);
  const [moreInfoOpen, setMoreInfoOpen] = useState(false);
  const [showTpHistory, setShowTpHistory] = useState(false);
  const [updateSlot, setUpdateSlot] = useState(false);
  const [editUpdateSlot, setEditUpdateSlot] = useState(false);
  const [page, setPage] = useState(1);
  const [useEffectReloading, setUseEffectReloading] = useState(false); // to reloading main page if some even occur

  const dispatchTrainerParams = useMemo(
    () => ({
      subject_enrollment_id: subjectEnrollmentId,
      course_enrollment_id: courseEnrollmentId,
      time_preference: includeTimingPreference,
      response_status: responseFilter?.id === -1 ? null : responseFilter?.id,
      ...tutorMatch?.studentData,
    }),
    [tutorMatch?.studentData, responseFilter, courseEnrollmentId, subjectEnrollmentId, includeTimingPreference],
  );

  useEffect(() => {
    dispatch(clearTutorMatchingData());
    if (courseEnrollmentId && subjectEnrollmentId) {
      dispatch(getStudentList({ course_enrollment_id: courseEnrollmentId, subject_enrollment_id: subjectEnrollmentId }));
    }
  }, [courseEnrollmentId, subjectEnrollmentId, dispatch]);

  useEffect(() => {
    if (courseEnrollmentId && subjectEnrollmentId && tutorMatch?.studentData) {
      const thunkAPI = dispatch(getTutorList(dispatchTrainerParams));

      return () => {
        thunkAPI.abort();
      };
    }
  }, [dispatchTrainerParams, dispatch, useEffectReloading]);

  // get-trainer-details has no exclude parameter; pagination is local, so earlier tutors are dropped here
  const allTutors = useMemo(() => {
    const list = Array.isArray(tutorMatch?.trainerData) ? tutorMatch.trainerData : [];
    if (!excludeTrainerIds.length) return list;
    const excluded = new Set(excludeTrainerIds.map(String));
    return list.filter((t) => !excluded.has(String(t.trainer_id)));
  }, [tutorMatch?.trainerData, excludeTrainerIds]);

  const paginatedTutors = useMemo(() => {
    const startIndex = (page - 1) * ITEMS_PER_PAGE;
    return allTutors.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [allTutors, page]);

  useEffect(() => {
    setPage(1);
  }, [allTutors]);

  return (
    <Box>
      {/* ---------- SEARCH OPTIONS ---------- */}
      <Stack direction={{ xs: 'column', sm: 'row' }} gap={2} alignItems={{ xs: 'stretch', sm: 'center' }} mb={2}>
        <Chip
          icon={<Icon icon="solar:users-group-rounded-bold" />}
          label={`${allTutors.length} Available Tutors`}
          color="primary"
          variant="filled"
          sx={{ fontWeight: 600, alignSelf: { xs: 'flex-start', sm: 'center' } }}
        />
        <Box sx={{ flex: 1 }} />
        <Paper
          variant="outlined"
          sx={{
            px: 1.5,
            py: 0.5,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            bgcolor: includeTimingPreference ? 'primary.lighter' : 'transparent',
            transition: '0.3s',
          }}
        >
          <Typography variant="subtitle2" fontWeight={700} fontSize={'0.8rem'}>
            TIME PREFERENCE
          </Typography>
          <Switch checked={includeTimingPreference} onChange={(e) => setIncludeTimingPreference(e.target.checked)} />
        </Paper>
        <GlobalSelectFieldWithoutFormikNew
          data={RESPONSE_OPTIONS}
          keys={{
            itemId: 'id',
            itemValue: 'label',
          }}
          selectedDetails={responseFilter}
          setChange={setResponseFilter}
          sx={{ minWidth: { xs: '100%', sm: 160 } }}
          label={'Response'}
        />
      </Stack>

      {/* ---------- SELECTED STUDENT INFO ---------- */}
      <Box mb={3}>
        {tutorMatchingLoading && !tutorMatch?.studentData ? (
          <Skeleton variant="rounded" height={100} />
        ) : tutorMatch?.studentData ? (
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: 3,
              bgcolor: 'grey.50',
              border: '1px dashed',
              borderColor: 'primary.main',
            }}
          >
            <Grid container spacing={2}>
              <TutorMatchingStudentDetails student={tutorMatch?.studentData} />
            </Grid>
          </Paper>
        ) : null}
      </Box>

      {/* ================= TUTORS LIST (FULL WIDTH) ================= */}
      <Box mb={2} display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="h6" fontWeight={700}>
          Matching Tutors
        </Typography>
      </Box>

      <Stack spacing={3}>
        {tutorMatchingLoading ? (
          [1, 2, 3].map((i) => <Skeleton key={i} variant="rounded" height={200} sx={{ borderRadius: 4 }} />)
        ) : allTutors.length ? (
          paginatedTutors.map((tutor) => (
            <TutorCard
              key={tutor.trainer_id || tutor.id}
              tutor={tutor}
              setUpdateSlot={setUpdateSlot}
              onViewProfile={(t) => {
                setMoreInfoOpen(true);
                setRowDetails(t);
              }}
              onTpHistory={(t) => {
                setShowTpHistory(true);
                setRowDetails(t);
              }}
              onAssignTutor={(t) => {
                setAssignTutorOpen(true);
                setRowDetails(t);
              }}
              setEditUpdateSlot={setEditUpdateSlot}
            />
          ))
        ) : (
          <Paper sx={{ p: 8, textAlign: 'center', borderRadius: 4, bgcolor: 'grey.50' }}>
            <Icon icon="solar:magnifer-zoom-out-bold-duotone" width={60} color="gray" />
            <Typography variant="h6" color="text.secondary" mt={2}>
              No matching tutors found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Try adjusting your filters or timing preferences
            </Typography>
          </Paper>
        )}
      </Stack>
      {allTutors.length > ITEMS_PER_PAGE && (
        <Stack alignItems="flex-end" mt={3}>
          <Pagination
            count={Math.ceil(allTutors.length / ITEMS_PER_PAGE)}
            page={page}
            onChange={(_, value) => setPage(value)}
            variant="outlined"
            shape="rounded"
            color="secondary"
          />
        </Stack>
      )}

      {/* MODALS */}
      {Boolean(updateSlot) && (
        <GlobalDialog
          content={<UpdateSlots trainer_id={updateSlot?.trainer_id ?? null} setEditUpdateSlot={setEditUpdateSlot} />}
          closeButton
          maxWidth={'md'}
          title={'Update Slot'}
          open={!editUpdateSlot && Boolean(updateSlot)}
          setOpen={setUpdateSlot}
        />
      )}
      {Boolean(editUpdateSlot) && (
        <GlobalDialog
          content={
            <EditUpdateSlot row={editUpdateSlot} setOpen={setEditUpdateSlot} setUseEffectReloading={setUseEffectReloading} />
          }
          closeButton
          maxWidth={'md'}
          title={'Edit Slot'}
          open={Boolean(editUpdateSlot)}
          setOpen={setEditUpdateSlot}
        />
      )}
      {assignTutorOpen && (
        <AssignTutor
          open={assignTutorOpen}
          setOpen={setAssignTutorOpen}
          isDisable={false}
          subjectEnrollmentId={subjectEnrollmentId}
          rowDetails={rowDetails}
          setUseEffectReloading={setUseEffectReloading}
          onAssigned={onAssigned}
        />
      )}
      {moreInfoOpen && <TutorDetails open={moreInfoOpen} setOpen={setMoreInfoOpen} rowDetails={rowDetails} />}
      {showTpHistory && <TpDetails open={showTpHistory} setOpen={setShowTpHistory} rowDetails={rowDetails} />}
    </Box>
  );
};

export default TutorMatchPanel;
